// Shared form-fields surface for Milestone Create/Edit. Consumed by
// MilestoneFormModal (write), MilestoneInlineExpand (write), and
// MilestoneDetailModal (read-only). The deliverables-in-this-milestone
// picker is supplied via an optional `deliverablesSlot` so the form
// fields don't need to know how the picker resolves milestone-scope
// state; on Create there is no milestone yet, so the slot is empty
// and a hint sentence renders in its place.

import { type ChangeEvent, type ReactNode } from "react";
import EvidenceLinksField from "../shared/EvidenceLinksField";
import CurrencyInput from "../../CurrencyInput";
import { compareSowNumbers } from "../shared/sowPickerSearch";
import type { Sow } from "../../../../types";
import type {
  MilestoneFieldErrors,
  MilestoneFormState,
} from "./milestoneFormState";

export interface MilestoneFormFieldsProps {
  value: MilestoneFormState;
  onChange: (patch: Partial<MilestoneFormState>) => void;
  errors?: MilestoneFieldErrors;
  disabled?: boolean;
  idPrefix: string;
  /** Optional slot for the MilestoneDeliverablesPicker. Empty on Create
   *  (the milestone has no id yet — link deliverables after saving). */
  deliverablesSlot?: ReactNode;
  /** SOWs on the same contract (supplied by the Tab). Active SOWs (non-
   *  archived, non-cancelled) drive the dropdown options. A currently-
   *  attached SOW that is archived/cancelled is still shown so the user
   *  can see what they're attached to — labelled with a status suffix. */
  sows?: Sow[];
}


export default function MilestoneFormFields({
  value,
  onChange,
  errors = {},
  disabled,
  idPrefix,
  deliverablesSlot,
  sows,
}: MilestoneFormFieldsProps) {
  const patch =
    <K extends keyof MilestoneFormState>(field: K) =>
    (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) =>
      onChange({ [field]: e.target.value } as Pick<MilestoneFormState, K>);

  // Active SOWs only — archived/cancelled excluded from the picker so
  // the user can't *newly* attach to a non-current SOW. The currently-
  // attached SOW is force-included even when archived/cancelled, with
  // a status suffix on the label, so the form reflects stored state.
  const sowOptions: { id: string; label: string }[] = (() => {
    if (!sows || sows.length === 0) return [];
    const active = sows.filter(
      (s) => !s.archivedAt && s.status !== "cancelled",
    );
    const sorted = [...active].sort((a, b) =>
      compareSowNumbers(a.number, b.number),
    );
    const opts = sorted.map((s) => ({
      id: s.id,
      label: `${s.number} · ${s.name}`,
    }));
    if (value.sowId && !opts.some((o) => o.id === value.sowId)) {
      const attached = sows.find((s) => s.id === value.sowId);
      if (attached) {
        const suffix = attached.archivedAt
          ? " (archived)"
          : attached.status === "cancelled"
          ? " (cancelled)"
          : "";
        opts.push({
          id: attached.id,
          label: `${attached.number} · ${attached.name}${suffix}`,
        });
      }
    }
    return opts;
  })();

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
        <div>
          <label htmlFor={`${idPrefix}-number`} className="label">
            Milestone # <span className="text-red-600">*</span>
          </label>
          <input
            id={`${idPrefix}-number`}
            type="text"
            inputMode="text"
            maxLength={5}
            className="input"
            value={value.milestoneNumberInput}
            onChange={(e) =>
              onChange({ milestoneNumberInput: e.target.value })
            }
            disabled={disabled}
            aria-invalid={!!errors.milestoneNumberInput}
            aria-describedby={`${idPrefix}-number-help`}
          />
          {errors.milestoneNumberInput ? (
            <p className="mt-1 text-caption text-red-600 dark:text-red-400">
              {errors.milestoneNumberInput}
            </p>
          ) : (
            <p
              id={`${idPrefix}-number-help`}
              className="mt-1 text-caption text-slate-500 dark:text-slate-400"
            >
              Number, optionally with a trailing letter to slot in between — e.g.
              13a comes right after 13.
            </p>
          )}
        </div>
        <div>
          <label htmlFor={`${idPrefix}-name`} className="label">
            Name <span className="text-red-600">*</span>
          </label>
          <input
            id={`${idPrefix}-name`}
            className="input"
            value={value.name}
            onChange={patch("name")}
            disabled={disabled}
            maxLength={200}
            aria-invalid={!!errors.name}
          />
          {errors.name && (
            <p className="mt-1 text-caption text-red-600 dark:text-red-400">
              {errors.name}
            </p>
          )}
        </div>
      </div>

      <div>
        <label htmlFor={`${idPrefix}-description`} className="label">
          Description
        </label>
        <textarea
          id={`${idPrefix}-description`}
          className="input min-h-[60px]"
          value={value.description}
          onChange={patch("description")}
          disabled={disabled}
          maxLength={2000}
          aria-invalid={!!errors.description}
        />
        {errors.description && (
          <p className="mt-1 text-caption text-red-600 dark:text-red-400">
            {errors.description}
          </p>
        )}
      </div>

      <div>
        <label
          htmlFor={`${idPrefix}-acceptance-criteria`}
          className="label"
        >
          Acceptance criteria / evidence required
        </label>
        <textarea
          id={`${idPrefix}-acceptance-criteria`}
          className="input min-h-[80px]"
          value={value.acceptanceCriteria}
          onChange={patch("acceptanceCriteria")}
          disabled={disabled}
          maxLength={2000}
          placeholder="e.g. UAT sign-off by ICT delivery lead; performance benchmark met per SOW §4.3; supplier's test report attached to evidence link."
          aria-invalid={!!errors.acceptanceCriteria}
        />
        {errors.acceptanceCriteria && (
          <p className="mt-1 text-caption text-red-600 dark:text-red-400">
            {errors.acceptanceCriteria}
          </p>
        )}
      </div>

      {sowOptions.length > 0 && (
        <div>
          <label htmlFor={`${idPrefix}-sow`} className="label">
            SOW
          </label>
          <select
            id={`${idPrefix}-sow`}
            data-testid="milestone-sow-picker"
            className="input"
            value={value.sowId ?? ""}
            onChange={(e) =>
              onChange({ sowId: e.target.value === "" ? null : e.target.value })
            }
            disabled={disabled}
          >
            <option value="">— None —</option>
            {sowOptions.map((s) => (
              <option key={s.id} value={s.id}>
                {s.label}
              </option>
            ))}
          </select>
        </div>
      )}

      <div>
        <label htmlFor={`${idPrefix}-planned`} className="label">
          Planned date <span className="text-red-600">*</span>
        </label>
        <input
          id={`${idPrefix}-planned`}
          type="date"
          className="input"
          value={value.plannedDate}
          onChange={patch("plannedDate")}
          disabled={disabled}
          aria-invalid={!!errors.plannedDate}
        />
        {errors.plannedDate && (
          <p className="mt-1 text-caption text-red-600 dark:text-red-400">
            {errors.plannedDate}
          </p>
        )}
      </div>

      <div>
        <label htmlFor={`${idPrefix}-payment`} className="label">
          Payment amount
        </label>
        <CurrencyInput
          id={`${idPrefix}-payment`}
          value={typeof value.paymentAmount === "number" ? value.paymentAmount : 0}
          onChange={(n) => onChange({ paymentAmount: n })}
          disabled={disabled}
          allowDecimal
        />
        <p className="mt-1 text-caption text-slate-500 dark:text-slate-400">
          Enter $0 for an acceptance-only milestone (no payment tied).
        </p>
      </div>

      <div>
        <label className="label">Deliverables in this milestone</label>
        <p className="mt-0.5 text-caption text-slate-500 dark:text-slate-400">
          Tick the deliverables that belong to this milestone. Deliverables
          already in another milestone show disabled with a hint.
        </p>
        {deliverablesSlot ?? (
          <p className="text-caption text-slate-500 dark:text-slate-400">
            Save the milestone first, then link deliverables.
          </p>
        )}
      </div>

      <EvidenceLinksField
        value={value.evidenceLinks}
        onChange={(v) => onChange({ evidenceLinks: v })}
        disabled={disabled}
      />

      <div>
        <label htmlFor={`${idPrefix}-notes`} className="label">
          Notes
        </label>
        <textarea
          id={`${idPrefix}-notes`}
          className="input min-h-[80px]"
          value={value.notes}
          onChange={patch("notes")}
          disabled={disabled}
          maxLength={2000}
        />
      </div>
    </div>
  );
}
